import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Checkbox,
    Flex,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Thead,
    Tr,
    Th,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    Icon,
    Portal,
    HStack,
} from '@chakra-ui/react';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { TbArchive, TbPlayerPlay, TbTrash } from 'react-icons/tb';
import { useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import SessionContext from '../../../contexts/SessionContext';
import { isInventoryAccessDeniedError } from '../../../utils/inventoryAccess';
import type { InventoryItemEntry } from '../../../hooks/inventory/useInventoryFeed';
import Pagination from '../../../components/Pagination';
import ResizableTh from '../../../components/ResizableTh';
import { formatPrice } from '../../../functions';
import {
    formatInventoryPartTypeLabel,
    formatInventoryUnitLabel,
    getInventoryStatusLabel,
    getInventoryStatusBadgeColors,
} from '../../../utils/inventoryConstants';
import MfgPartNumberWithCopy from '../../../components/inventory/MfgPartNumberWithCopy';
import { getAlternateCount, getPrimaryPartNumber } from '../../../utils/inventoryPartNumbers';
import InventoryBulkActionBar from '../../../components/inventory/InventoryBulkActionBar';
import OrderHoverCheckbox from '../../../components/orders/OrderHoverCheckbox';
import type { InventoryListPageModel } from './useInventoryListPage';

const CATEGORY_LABELS: Record<string, string> = {
    part: 'Part',
    material: 'Material',
    liquid: 'Liquid',
};

export function InventoryListTable({ page }: { page: InventoryListPageModel }) {
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const {
        isLoading,
        isRefetching,
        error,
        pageItems,
        searchQuery,
        tableSettingsSx,
        colWidths,
        updateColWidth,
        scrollContainerRef,
        handleRowClick,
        handleDeleteRequest,
        handleRowStatusChange,
        currentPage,
        setCurrentPage,
        totalPages,
        hasNextPage,
        selectedItemIds,
        selectedStatuses,
        toggleItemSelected,
        toggleSelectAllOnPage,
        allPageSelected,
        isPageSelectionIndeterminate,
        clearSelection,
        handleBulkAction,
        isBulkActionLoading,
    } = page;

    useEffect(() => {
        if (!error || !isInventoryAccessDeniedError(error)) return;
        queryClient.invalidateQueries({
            queryKey: ['/account/validate', sessionContext?.sessionId ?? null],
        });
    }, [error, queryClient, sessionContext?.sessionId]);

    return (
        <Box flex={1} display="flex" flexDirection="column" overflow="hidden" minH={0}>
            {!error && pageItems.length === 0 && (isLoading || isRefetching) ? (
                <Flex justify="center" align="center" h="400px">
                    <Spinner size="xl" color="blue.500" />
                </Flex>
            ) : error ? (
                <Alert status="error">
                    <AlertIcon />
                    {isInventoryAccessDeniedError(error)
                        ? 'Inventory is not enabled for this account.'
                        : 'Failed to load inventory. Please try again.'}
                </Alert>
            ) : pageItems.length > 0 ? (
                <>
                    <Box position="relative" flex={1} minH={0} display="flex" flexDirection="column">
                        <Box
                            ref={scrollContainerRef}
                            flex={1}
                            minH={0}
                            overflow="auto"
                            borderTop="1px solid"
                            borderColor="gray.200"
                            sx={{
                                ...tableSettingsSx,
                                overscrollBehavior: 'contain',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 transparent',
                                '& table': { width: 'max-content !important', minWidth: '100%' },
                                '& thead': {
                                    zIndex: '3 !important',
                                },
                                '& th': {
                                    ...(tableSettingsSx['& th'] || {}),
                                    borderBottomWidth: '0 !important',
                                    boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                                    bg: 'gray.50',
                                },
                                '& th:last-of-type': {
                                    position: 'sticky',
                                    right: 0,
                                    zIndex: 5,
                                    bg: 'gray.50',
                                },
                                '& td:last-of-type': {
                                    position: 'sticky',
                                    right: 0,
                                    zIndex: 2,
                                    bg: 'white',
                                },
                                '& tbody tr:hover td:last-of-type': {
                                    bg: 'gray.50',
                                },
                                '& tr:hover .order-row-checkbox': {
                                    opacity: '1 !important',
                                    pointerEvents: 'auto !important',
                                },
                                '& tr[data-selected="true"] td:last-of-type': {
                                    bg: 'blue.50',
                                },
                                '& tr[data-selected="true"]:hover td:last-of-type': {
                                    bg: 'blue.100',
                                },
                            }}
                        >
                            <Table
                                variant="simple"
                                size="sm"
                                minW="100%"
                                sx={
                                    Object.keys(colWidths).length > 0
                                        ? {
                                              tableLayout: 'fixed',
                                              '& td': {
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                              },
                                          }
                                        : undefined
                                }
                            >
                                <Thead position="sticky" top={0} zIndex={1}>
                                    <Tr bg="gray.50">
                                        <ResizableTh
                                            columnKey="name"
                                            storedWidth={colWidths.name}
                                            onResize={updateColWidth}
                                            minWidth={180}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            pl={6}
                                            pr={3}
                                            textTransform="uppercase"
                                        >
                                            <HStack spacing={3}>
                                                <Checkbox
                                                    aria-label="Select all items on this page"
                                                    isChecked={allPageSelected}
                                                    isIndeterminate={isPageSelectionIndeterminate}
                                                    isDisabled={pageItems.length === 0}
                                                    size="sm"
                                                    colorScheme="blue"
                                                    sx={{
                                                        '& .chakra-checkbox__control': {
                                                            width: '14px',
                                                            height: '14px',
                                                        },
                                                        '& .chakra-checkbox__icon': {
                                                            fontSize: '9px',
                                                        },
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={() => toggleSelectAllOnPage()}
                                                />
                                                <Text as="span">Name</Text>
                                            </HStack>
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="manufacturerPartNumber"
                                            storedWidth={colWidths.manufacturerPartNumber}
                                            onResize={updateColWidth}
                                            minWidth={140}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                        >
                                            Mfg. Part Number
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="status"
                                            storedWidth={colWidths.status}
                                            onResize={updateColWidth}
                                            minWidth={100}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                        >
                                            Status
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="inStockQuantity"
                                            storedWidth={colWidths.inStockQuantity}
                                            onResize={updateColWidth}
                                            minWidth={110}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                        >
                                            In-Stock Qty
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="category"
                                            storedWidth={colWidths.category}
                                            onResize={updateColWidth}
                                            minWidth={100}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                        >
                                            Category
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="partType"
                                            storedWidth={colWidths.partType}
                                            onResize={updateColWidth}
                                            minWidth={100}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                        >
                                            Part Type
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="supplierName"
                                            storedWidth={colWidths.supplierName}
                                            onResize={updateColWidth}
                                            minWidth={120}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                        >
                                            Supplier
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="averageUnitCost"
                                            storedWidth={colWidths.averageUnitCost}
                                            onResize={updateColWidth}
                                            minWidth={100}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                        >
                                            Avg. Unit Cost
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="locationTitle"
                                            storedWidth={colWidths.locationTitle}
                                            onResize={updateColWidth}
                                            minWidth={140}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                        >
                                            Location
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="origin"
                                            storedWidth={colWidths.origin}
                                            onResize={updateColWidth}
                                            minWidth={100}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                        >
                                            Origin
                                        </ResizableTh>
                                        <Th w="60px" minW="60px" py={2.5} px={3} />
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {pageItems.map((item: InventoryItemEntry, rowIndex: number) => {
                                        const badgeColors = getInventoryStatusBadgeColors(item);
                                        const isSelected = selectedItemIds.has(item._id);
                                        return (
                                            <Tr
                                                key={item._id}
                                                color="gray.700"
                                                data-selected={isSelected ? 'true' : undefined}
                                                bg={isSelected ? 'blue.50' : undefined}
                                                _hover={{
                                                    bg: isSelected ? 'blue.100' : 'gray.50',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={(e) => handleRowClick(item, e, rowIndex)}
                                            >
                                                <Td py={3} pl={6} pr={3}>
                                                    <HStack spacing={3} minW={0}>
                                                        <OrderHoverCheckbox
                                                            orderId={item._id}
                                                            rowIndex={rowIndex}
                                                            isChecked={isSelected}
                                                            onToggle={toggleItemSelected}
                                                        />
                                                        <Text
                                                            as="span"
                                                            noOfLines={1}
                                                            title={item.name}
                                                        >
                                                            {item.name}
                                                        </Text>
                                                    </HStack>
                                                </Td>
                                                <Td py={3} px={3}>
                                                    <MfgPartNumberWithCopy
                                                        partNumber={getPrimaryPartNumber(item)}
                                                        alternateCount={getAlternateCount(item)}
                                                        color="gray.700"
                                                        fontWeight="normal"
                                                    />
                                                </Td>
                                                <Td py={3} px={3}>
                                                    <Badge
                                                        bg={badgeColors.bg}
                                                        color={badgeColors.color}
                                                        fontSize="xs"
                                                        fontWeight="semibold"
                                                        borderRadius="lg"
                                                        py={1}
                                                        px={2}
                                                        minH="28px"
                                                        display="inline-flex"
                                                        alignItems="center"
                                                    >
                                                        {getInventoryStatusLabel(item)}
                                                    </Badge>
                                                </Td>
                                                <Td py={3} px={3}>
                                                    {item.inStockQuantity}{' '}
                                                    {formatInventoryUnitLabel(item.unit)}
                                                </Td>
                                                <Td py={3} px={3}>
                                                    {CATEGORY_LABELS[item.category] ?? item.category}
                                                </Td>
                                                <Td py={3} px={3}>
                                                    {formatInventoryPartTypeLabel(item.partType)}
                                                </Td>
                                                <Td py={3} px={3}>
                                                    {item.supplierName}
                                                </Td>
                                                <Td py={3} px={3}>
                                                    {formatPrice(item.averageUnitCost)}
                                                </Td>
                                                <Td py={3} px={3}>
                                                    {item.locationTitle}
                                                </Td>
                                                <Td py={3} px={3}>
                                                    {item.origin?.trim() || '—'}
                                                </Td>
                                                <Td
                                                    py={3}
                                                    px={3}
                                                    w="60px"
                                                    minW="60px"
                                                    borderBottomWidth="1px"
                                                    borderColor="gray.100"
                                                    bg={isSelected ? 'blue.50' : undefined}
                                                >
                                                    <Flex justify="center" align="center">
                                                        <Menu strategy="fixed">
                                                            <MenuButton
                                                                as={IconButton}
                                                                aria-label="Options"
                                                                icon={
                                                                    <Icon
                                                                        as={
                                                                            HiOutlineDotsVertical as React.ElementType
                                                                        }
                                                                        w={4}
                                                                        h={4}
                                                                    />
                                                                }
                                                                size="xs"
                                                                variant="outline"
                                                                colorScheme="gray"
                                                                borderRadius="lg"
                                                                borderColor="gray.300"
                                                                bg="white"
                                                                _hover={{
                                                                    bg: 'gray.50',
                                                                    borderColor: 'gray.400',
                                                                }}
                                                                _active={{
                                                                    bg: 'gray.100',
                                                                    borderColor: 'gray.400',
                                                                }}
                                                                _focus={{ boxShadow: 'outline' }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                            <Portal>
                                                                <MenuList
                                                                    py={1}
                                                                    minW="160px"
                                                                    zIndex={1400}
                                                                    bg="white"
                                                                    boxShadow="none"
                                                                >
                                                                    {item.status === 'discontinued' ? (
                                                                        <MenuItem
                                                                            fontSize="sm"
                                                                            px={3}
                                                                            py={2}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRowStatusChange(
                                                                                    item,
                                                                                    'active',
                                                                                );
                                                                            }}
                                                                        >
                                                                            <HStack spacing={2}>
                                                                                <Icon
                                                                                    as={
                                                                                        TbPlayerPlay as React.ElementType
                                                                                    }
                                                                                    boxSize={4}
                                                                                    color="gray.500"
                                                                                />
                                                                                <Text>Activate</Text>
                                                                            </HStack>
                                                                        </MenuItem>
                                                                    ) : (
                                                                        <MenuItem
                                                                            fontSize="sm"
                                                                            px={3}
                                                                            py={2}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRowStatusChange(
                                                                                    item,
                                                                                    'discontinued',
                                                                                );
                                                                            }}
                                                                        >
                                                                            <HStack spacing={2}>
                                                                                <Icon
                                                                                    as={
                                                                                        TbArchive as React.ElementType
                                                                                    }
                                                                                    boxSize={4}
                                                                                    color="gray.500"
                                                                                />
                                                                                <Text>Discontinue</Text>
                                                                            </HStack>
                                                                        </MenuItem>
                                                                    )}
                                                                    <MenuItem
                                                                        fontSize="sm"
                                                                        px={3}
                                                                        py={2}
                                                                        color="red.600"
                                                                        _hover={{ bg: 'red.50' }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteRequest(item);
                                                                        }}
                                                                    >
                                                                        <HStack spacing={2}>
                                                                            <Icon
                                                                                as={
                                                                                    TbTrash as React.ElementType
                                                                                }
                                                                                boxSize={4}
                                                                                color="red.500"
                                                                            />
                                                                            <Text>Delete</Text>
                                                                        </HStack>
                                                                    </MenuItem>
                                                                </MenuList>
                                                            </Portal>
                                                        </Menu>
                                                    </Flex>
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                        {selectedItemIds.size > 0 && (
                            <InventoryBulkActionBar
                                selectedCount={selectedItemIds.size}
                                selectedStatuses={selectedStatuses}
                                onClearSelection={clearSelection}
                                onAction={handleBulkAction}
                                isLoading={isBulkActionLoading}
                            />
                        )}
                    </Box>
                    <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            hasNextPage={hasNextPage}
                            onPageChange={setCurrentPage}
                            isLoading={isRefetching}
                        />
                    </Box>
                </>
            ) : (
                <Flex direction="column" align="center" justify="center" h="300px" px={6}>
                    <Text color="gray.500" fontSize="sm">
                        {searchQuery
                            ? 'No inventory items match your search.'
                            : 'No inventory items found.'}
                    </Text>
                </Flex>
            )}
        </Box>
    );
}
